// src/components/admin/pagos/planEditor/ConceptosSection.js
import React from 'react'
import { Plus, Trash2, Shirt } from 'lucide-react'
import { Button, Input, Label, Combobox } from '../../../ui'
import { formatMoney } from '../../../../utils/helpers'
import { totalConceptosGuardables } from '../../../../lib/services/pagosPlanHelpers'

const ConceptosSection = ({ conceptos = [], conceptoOptions = [], prendaConceptoIds = new Set(), prendasDisponibles = [], onAgregarUniforme, onQuitarUniforme, onChange }) => {
  const update = (i, patch) => onChange(conceptos.map((c, idx) => idx === i ? { ...c, ...patch } : c))
  const add = () => onChange([...conceptos, { concepto_id: '', monto: '' }])
  const remove = (i) => onChange(conceptos.filter((_, idx) => idx !== i))

  // Los conceptos de prendas (uniformes) no se eligen aquí: se gestionan desde Uniformes.
  // Un concepto va UNA vez: aquí se declara de qué se compone el costo, no cuántos pagos
  // son. Cobrarlo en varias exhibiciones es cosa de Parcialidades. Los ya usados se
  // muestran deshabilitados con su motivo (esconderlos deja al usuario buscándolos).
  const usados = new Set(conceptos.map((c) => c.concepto_id).filter(Boolean))
  const addableOptions = conceptoOptions.filter((o) => !prendaConceptoIds.has(o.value))
  const conceptoLabel = (id) => conceptoOptions.find((o) => o.value === id)?.label || 'Concepto'

  // Separar conceptos editables de los de prenda, conservando su índice original
  const rows = conceptos.map((c, i) => ({ c, i }))
  const editables = rows.filter(({ c }) => !prendaConceptoIds.has(c.concepto_id))
  const prendas = rows.filter(({ c }) => prendaConceptoIds.has(c.concepto_id))
  const prendaSubtotal = prendas.reduce((s, { c }) => s + Number(c.monto || 0), 0)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-base font-semibold">Conceptos</Label>
        <Button type="button" variant="outline" size="sm" onClick={add}><Plus className="mr-1 h-4 w-4" /> Concepto</Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Cada concepto va una sola vez, con su costo total. Para cobrarlo en varios pagos, usa el paso <strong>Parcialidades</strong>.
      </p>

      {editables.length === 0 && prendas.length === 0 ? (
        <p className="rounded-lg border border-dashed py-6 text-center text-sm text-muted-foreground">
          Agrega los conceptos que componen el costo del plan.
        </p>
      ) : (
        <div className="space-y-3">
          {/* Conceptos editables */}
          {editables.length === 0 ? (
            <p className="text-sm text-muted-foreground">Agrega los conceptos del plan (inscripción, mensualidades, etc.).</p>
          ) : (
            <div className="space-y-2">
              {editables.map(({ c, i }) => (
                <div key={i} className="flex items-end gap-2">
                  <div className="min-w-0 flex-1 space-y-1">
                    <Label className="text-xs">Concepto</Label>
                    <Combobox
                      className="w-full"
                      options={addableOptions.map((o) => (
                        o.value === c.concepto_id || !usados.has(o.value)
                          ? o
                          : { ...o, disabled: true, hint: 'ya está en el plan' }
                      ))}
                      value={c.concepto_id}
                      onValueChange={(v) => {
                        const opt = conceptoOptions.find((o) => o.value === v)
                        update(i, { concepto_id: v, monto: c.monto || opt?.montoDefault || '' })
                      }}
                      searchPlaceholder="Buscar concepto..."
                    />
                  </div>
                  <div className="w-24 space-y-1 sm:w-32">
                    <Label className="text-xs">Monto</Label>
                    <Input type="number" min="0" step="0.01" value={c.monto}
                      onChange={(e) => update(i, { monto: e.target.value })} placeholder="$0" />
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="h-10 w-10 shrink-0 p-0 text-destructive" onClick={() => remove(i)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Uniformes: bloque propio dentro de Conceptos, no un paso aparte.
              El total del plan es UNO: si su composicion se armara en dos pasos, el
              marcador "Cuadra" cambiaria al avanzar y se perderia de vista.
              Manana Inventario seria otro bloque igual, sin agregar pasos.
              Su monto no se edita aqui: lo manda el precio de la prenda. */}
          {(prendas.length > 0 || (onAgregarUniforme && prendasDisponibles.length > 0)) && (
            <div className="space-y-2 rounded-lg border border-dashed bg-muted/40 p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Shirt className="h-3.5 w-3.5" /> Uniformes
                </span>
                <span className="text-xs tabular-nums text-muted-foreground">{formatMoney(prendaSubtotal)}</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Su costo sale del catalogo de Uniformes. Aqui eliges cuales incluye el plan.
              </p>

              {prendas.length > 0 && (
                <div className="space-y-1.5">
                  {prendas.map(({ c }) => (
                    <div key={c.concepto_id} className="flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2">
                      <span className="min-w-0 truncate text-sm">{conceptoLabel(c.concepto_id)}</span>
                      <div className="flex shrink-0 items-center gap-1">
                        <span className="text-sm tabular-nums">{formatMoney(c.monto)}</span>
                        {onQuitarUniforme && (
                          <Button
                            type="button" variant="ghost" size="sm"
                            className="h-8 w-8 p-0 text-destructive"
                            aria-label={`Quitar ${conceptoLabel(c.concepto_id)} del plan`}
                            onClick={() => onQuitarUniforme(c.concepto_id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {onAgregarUniforme && (
                prendasDisponibles.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 pt-0.5">
                    {prendasDisponibles.map((p) => (
                      <Button
                        key={p.id} type="button" variant="outline" size="sm" className="h-8"
                        onClick={() => onAgregarUniforme(p)}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        {p.nombre} · {formatMoney(p.precio)}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    Todas las prendas del catalogo ya estan en este plan.
                  </p>
                )
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-end gap-3 border-t pt-2">
        <span className="text-sm font-semibold">Total: {formatMoney(totalConceptosGuardables(conceptos))}</span>
      </div>
    </div>
  )
}

export default ConceptosSection
